import * as React from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import { IPackLineDefaultFormValues, IPackLineFormValues, packLineFormSchema } from '../../../../@types/model/masterData/packLine/packLineFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IPackLineFormProps {
    isLoading : boolean;
    initialValues : IPackLineFormValues | IPackLineDefaultFormValues;
    onSubmit : (values : IPackLineFormValues) => void;
    onClose : () => void;
}

const PackLineForm = (props : IPackLineFormProps) : React.ReactElement => {
    const schema = packLineFormSchema;
    const { initialValues, onSubmit, onClose, isLoading } = props;

    const { handleSubmit, control, formState: { isValid, isDirty } } = useForm<IPackLineFormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const auth = useAppSelector(state => state.auth);
    const sites = useAppSelector(state => state.masterData.sites);

    const siteOptions : Array<IOptionType> = useMemo(() => {
        return sites?.filter(x => x.isActive && auth.session?.user.siteIds.some(y => y === x.id))
            .map(x => ({ label: `(${x.code})  ${x.description}`, value: x.id })) ?? [];
    }, [sites, auth.session?.user.siteIds]);

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(onSubmit)} className={'p20'}>
                <div className={'fdc aic'}>
                    <ControlledTextField
                        className={'mb10 w500'}
                        name={'code'}
                        label={'Code'}
                        control={control}
                        defaultValue={initialValues.code} />
                    <ControlledTextField
                        className={'mb10 w500'}
                        name={'name'}
                        label={'Name'}
                        control={control}
                        defaultValue={initialValues.name} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'site'}
                        label={'Site'}
                        control={control}
                        options={siteOptions}
                        defaultValues={initialValues.site ?? null} />
                    <div className={'fdr jcfs mb5'}>
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'isActive'}
                            label={'Is Active'}
                            control={control}
                            defaultValue={initialValues.isActive} />
                    </div>
                    <div className={'fdr ml10 ais jcfe pt10 pb10'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={!isDirty || !isValid || isLoading}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'} />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PackLineForm;
